// Which cards a play moved, and where: drawn off a deck, taken from a hand, passed along
// with a whole hand, thrown away, or milled off the top of a deck. Worked out by comparing
// the table before and after the play rather than by hooking each helper, so a new card
// that moves cards is described without anyone remembering to describe it.
//
// The moves ride in a synced "cards" event, so every reader here treats them as
// untrusted: names are coerced to strings and a malformed move paints as a harmless line.

function list(value) {
  return Array.isArray(value) ? value : [];
}

// Multiset difference, keeping the order of `items`: each id in `removed` cancels one copy.
function minus(items, removed) {
  const pool = new Map();
  removed.forEach((id) => pool.set(id, (pool.get(id) || 0) + 1));
  return items.filter((id) => {
    const left = pool.get(id) || 0;
    if (!left) return true;
    pool.set(id, left - 1);
    return false;
  });
}

// Multiset intersection, in the order of `items`.
function common(items, other) {
  return minus(items, minus(items, other));
}

// The table as a play found it. `held` is the hand array itself, not a copy: Bytt är bytt
// and Stolleken move whole arrays between seats, and with a standard deck full of
// duplicates, comparing contents alone would say a swap only moved the cards the two
// hands did not share.
export function tableSnapshot(players) {
  return list(players).map((player) => ({
    id: player.id,
    hand: [...list(player.hand)],
    deck: [...list(player.deck)],
    held: Array.isArray(player.hand) ? player.hand : null,
  }));
}

function move(kind, from, to, cardIds, nameOf) {
  return {
    kind,
    fromId: from?.id ?? null,
    fromName: from?.name ?? null,
    toId: to?.id ?? null,
    toName: to?.name ?? null,
    cardIds: [...cardIds],
    cardNames: cardIds.map((id) => String(nameOf(id))),
  };
}

// Everything that moved between `before` and the table as it is now, in the order a
// reader wants it told: whole hands first, then single cards taken or passed, cards
// thrown away, decks milled, and last what was drawn.
export function cardMoves(before, players, nameOf = (id) => id) {
  const was = new Map(list(before).map((entry) => [entry.id, entry]));
  const seats = list(players).filter((player) => was.has(player.id));
  const entries = seats.map((player) => was.get(player.id));
  const seatHolding = (entry) => seats.find((player) => player.hand === entry.held);

  const changes = seats.map((player) => {
    const entry = was.get(player.id);
    // The hand array this seat holds now, as it was before: its own, or another seat's.
    const source = entries.find((other) => other.held === player.hand) || entry;
    // Wherever this seat's own hand went, which is what its losses are measured against.
    const keeper = seatHolding(entry) || player;
    const deckOut = minus(entry.deck, player.deck);
    const gained = minus(player.hand, source.hand);
    const drawn = common(gained, deckOut);
    return {
      player,
      source,
      handedOver: source !== entry,
      drawn,
      milled: minus(deckOut, drawn),
      received: minus(gained, drawn),
      lost: minus(entry.hand, keeper.hand),
    };
  });
  // Got something back this play — a swap or a rotation — which makes a loss a pass
  // rather than something taken.
  changes.forEach((change) => {
    change.gotBack = change.handedOver || change.received.length > 0;
  });

  const seatOf = (id) => seats.find((player) => player.id === id);
  const moves = [];
  changes.forEach((change) => {
    if (change.handedOver) {
      moves.push(move("pass", seatOf(change.source.id), change.player, common(change.source.hand, change.player.hand), nameOf));
    }
  });
  changes.forEach((giver) => {
    let left = giver.lost;
    // Whoever got most of these cards gets them first, so one hand's cards are not
    // scattered over the table by an id that happens to sit in two hands.
    const takers = changes
      .filter((taker) => taker !== giver)
      .map((taker) => ({taker, overlap: common(left, taker.received).length}))
      .sort((first, second) => second.overlap - first.overlap);
    takers.forEach(({taker}) => {
      const moved = common(left, taker.received);
      if (!moved.length) return;
      taker.received = minus(taker.received, moved);
      left = minus(left, moved);
      moves.push(move(giver.gotBack ? "pass" : "take", giver.player, taker.player, moved, nameOf));
    });
    if (left.length) moves.push(move("discard", giver.player, null, left, nameOf));
  });
  changes.forEach((change) => {
    if (change.milled.length) moves.push(move("mill", change.player, null, change.milled, nameOf));
  });
  changes.forEach((change) => {
    if (change.drawn.length) moves.push(move("draw", null, change.player, change.drawn, nameOf));
  });
  return moves;
}

function namesOf(entry) {
  return list(entry?.cardNames).map(String);
}

// "Alfa", "Alfa och Beta", "Alfa, Beta och Gamma".
function listText(names) {
  if (names.length < 2) return names.join("");
  return `${names.slice(0, -1).join(", ")} och ${names[names.length - 1]}`;
}

function countText(count) {
  return count === 1 ? "ett kort" : `${count} kort`;
}

// The line the board and the history print. It names cards that were taken or thrown
// away — everyone saw them go — but only counts the ones that went into somebody's hand,
// because the TV must not read a player's hand out to the room.
export function cardMoveLine(entry) {
  const names = namesOf(entry);
  const from = String(entry?.fromName ?? "");
  const to = String(entry?.toName ?? "");
  switch (entry?.kind) {
    case "draw": return `${to} drog ${countText(names.length)}`;
    case "take": return `${to} tog ${listText(names)} från ${from}`;
    case "pass": return `${to} fick ${countText(names.length)} från ${from}`;
    case "discard": return `${from} slängde ${listText(names)}`;
    case "mill": return `${from} tappade ${listText(names)} ur leken`;
    default: return "Kort flyttades";
  }
}

// A full stop, unless the line already ends on one of its own: "Du drog Skål!" must not
// read "Skål!.".
function sentence(text) {
  return /[.!?]$/.test(text) ? text : `${text}.`;
}

// What the player's own phone says about a move, by name — it is their hand — or null
// when the move did not touch them.
export function cardMoveNotice(entry, playerId) {
  const line = noticeLine(entry, playerId);
  return line && sentence(line);
}

function noticeLine(entry, playerId) {
  const names = listText(namesOf(entry));
  const mine = (id) => Boolean(playerId) && id === playerId;
  const from = String(entry?.fromName ?? "");
  const to = String(entry?.toName ?? "");
  switch (entry?.kind) {
    case "draw": return mine(entry.toId) ? `Du drog ${names}` : null;
    case "take":
      if (mine(entry.toId)) return `Du tog ${names} från ${from}`;
      return mine(entry.fromId) ? `${to} tog ${names} från dig` : null;
    case "pass":
      if (mine(entry.toId)) return `Du fick ${names} från ${from}`;
      return mine(entry.fromId) ? `Du gav ${names} till ${to}` : null;
    case "discard": return mine(entry.fromId) ? `${names} slängdes ur din hand` : null;
    case "mill": return mine(entry.fromId) ? `${names} åkte ur din kortlek` : null;
    default: return null;
  }
}

const GAINED_KINDS = new Set(["draw", "take", "pass"]);

// The phone's card notice: the newest recent play that moved one of this player's
// cards, what it did to them, and the ids that reached their hand (for the "Ny" marks).
export function cardNoticeFor(events, playerId, now = Date.now(), maxAge = 12000) {
  if (!playerId) return null;
  const recent = list(events).filter((event) => event?.type === "cards" && now - event.at < maxAge);
  for (const event of recent.reverse()) {
    const moves = list(event.moves);
    const lines = moves.map((entry) => cardMoveNotice(entry, playerId)).filter(Boolean);
    if (!lines.length) continue;
    const who = event.playerId === playerId ? "Du" : String(event.playerName ?? "Någon");
    const card = event.cardName ? String(event.cardName) : "ett kort";
    return {
      id: String(event.id),
      title: `${who} spelade ${card}`,
      lines,
      gained: moves
        .filter((entry) => GAINED_KINDS.has(entry?.kind) && entry.toId === playerId)
        .flatMap((entry) => list(entry.cardIds).map(String)),
    };
  }
  return null;
}

// Which cards in a hand are the newly arrived ones. Drawn and taken cards land at the
// back of the hand, and a whole passed hand is new from end to end, so they are matched
// from the back, one copy per id.
export function freshHandFlags(hand, cardIds) {
  const pool = new Map();
  list(cardIds).forEach((id) => pool.set(id, (pool.get(id) || 0) + 1));
  const flags = list(hand).map(() => false);
  for (let index = flags.length - 1; index >= 0; index -= 1) {
    const left = pool.get(hand[index]) || 0;
    if (!left) continue;
    pool.set(hand[index], left - 1);
    flags[index] = true;
  }
  return flags;
}

// What the board stamps beside one seat for the cards a play moved there: a count in the
// big line, and the names only of what left the seat for good.
export function seatCardStamp(moves, playerId) {
  let gained = 0;
  let lost = 0;
  let milled = 0;
  const lines = [];
  list(moves).forEach((entry) => {
    const count = namesOf(entry).length;
    if (GAINED_KINDS.has(entry?.kind) && entry.toId === playerId) {
      gained += count;
      if (entry.kind !== "draw") lines.push(`från ${String(entry.fromName ?? "")}`);
    }
    if (entry?.fromId !== playerId) return;
    if (entry.kind === "mill") {
      milled += count;
      lines.push(namesOf(entry).join(", "));
    } else if (entry.kind === "take" || entry.kind === "discard") {
      lost += count;
      lines.push(namesOf(entry).join(", "));
    } else if (entry.kind === "pass") {
      lost += count;
    }
  });
  if (gained && lost) return {big: "Bytt!", lines: [`${lost} ut, ${gained} in`]};
  if (gained) return {big: `+${gained} kort`, lines};
  if (lost) return {big: `−${lost} kort`, lines};
  if (milled) return {big: `−${milled} ur leken`, lines};
  return null;
}
